/**
 * Cadastro manual de produto (útil no período de teste, antes de integrar o ERP). Ao digitar o EAN, a tela busca no
 * catálogo mestre: medicamento preenche sozinho (o cadastro do medicamento vem do mestre); não medicamento pergunta
 * "Completar com o catálogo Berytus?"; não achou: cadastro manual. Marcado como medicamento e fora do mestre: o item
 * fica aguardando a curadoria da Berytus.
 */
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { NewProductBody } from '@berytus/contracts';
import { api, HttpError } from '../api.ts';
import { useMe } from '../components/AuthGate.tsx';
import { ErrorBox } from '../components/ui.tsx';

const num = (v: string) => (v.trim() === '' ? undefined : Number(v.replace(/\./g, '').replace(',', '.')));

export function NewProductDialog({ onClose }: { onClose: () => void }) {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [f, setF] = useState({ sku: '', title: '', gtin: '', brand: '', category: '', cost: '', pmc: '', stock: '' });
  const [isMedicine, setIsMedicine] = useState(false);
  const [lookup, setLookup] = useState<{ gtin: string; found: boolean; isMedicine?: boolean; title?: string; brand?: string | null; ask?: boolean; applied?: boolean } | null>(null);
  const fill = (m: { title?: string; brand?: string | null }) => setF((x) => ({ ...x, title: m.title ?? x.title, brand: m.brand ?? x.brand }));
  const lookupGtin = async (gtin: string) => {
    const g = gtin.replace(/\D/g, '');
    if (g.length < 8 || lookup?.gtin === g) return;
    try {
      const r = await api<{ found: boolean; isMedicine?: boolean; title?: string; brand?: string | null }>(`/catalog/master-lookup?gtin=${g}`);
      if (r.found && r.isMedicine) { fill(r); setIsMedicine(true); setLookup({ gtin: g, ...r, applied: true }); }
      else setLookup({ gtin: g, ...r, ask: r.found });
    } catch { setLookup(null); }
  };
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  const create = useMutation({
    mutationFn: () => {
      const body: NewProductBody = {
        sku: f.sku.trim(), title: f.title.trim(), gtin: f.gtin.trim() || undefined, brand: f.brand.trim() || undefined,
        category: f.category.trim() || undefined, cost: num(f.cost), pmc: num(f.pmc), stock: num(f.stock),
        ...(isMedicine ? { isMedicine: true } : {}),
      };
      return api<{ id: string }>('/catalog/products', { method: 'POST', json: body });
    },
    onSuccess: (r) => {
      for (const k of ['products', 'categories', 'summary']) qc.invalidateQueries({ queryKey: [k] });
      qc.invalidateQueries({ queryKey: ['me'] });
      onClose();
      navigate(`/catalogo/${r.id}`);
    },
  });
  const limitHit = create.error instanceof HttpError && create.error.code === 'trial_product_limit';
  const submit = (e: FormEvent) => { e.preventDefault(); create.mutate(); };
  const t = me.trial;

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="drawer-overlay" />
        <Dialog.Content className="panel" aria-describedby={undefined}
          style={{ position: 'fixed', top: '8vh', left: '50%', transform: 'translateX(-50%)', width: 'min(560px, calc(100vw - 32px))', maxHeight: '84vh', overflow: 'auto', zIndex: 60 }}>
          <form onSubmit={submit} style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Dialog.Title asChild><h2 style={{ margin: 0 }}>Novo produto</h2></Dialog.Title>
              <span style={{ flex: 1 }} />
              <Dialog.Close className="icon-btn" aria-label="Fechar"><X size={18} /></Dialog.Close>
            </div>
            {t && <p className="small muted" style={{ margin: 0 }}>Período de teste: {t.usage.products} de {t.limits.products} produtos usados.</p>}
            <div className="cols" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
              <div className="field"><label htmlFor="np-sku">SKU</label><input className="input" id="np-sku" required autoFocus value={f.sku} onChange={set('sku')} /></div>
              <div className="field"><label htmlFor="np-gtin">EAN</label><input className="input" id="np-gtin" inputMode="numeric" value={f.gtin} onChange={set('gtin')} onBlur={(e) => lookupGtin(e.target.value)} /></div>
            </div>
            {lookup?.found && lookup.isMedicine && (
              <div className="notice small">Medicamento do catálogo Berytus: nome e marca preenchidos pelo mestre. O cadastro do medicamento vem do mestre; você informa preço, estoque e foto.</div>
            )}
            {lookup?.ask && (
              <div className="notice small" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <span>Achamos este EAN no catálogo Berytus: <b>{lookup.title}</b>. Completar com o catálogo Berytus?</span>
                <button type="button" className="btn sm" onClick={() => { fill(lookup); setLookup({ ...lookup, ask: false, applied: true }); }}>Sim, completar</button>
                <button type="button" className="btn sm quiet" onClick={() => setLookup({ ...lookup, ask: false })}>Não</button>
              </div>
            )}
            {lookup && !lookup.found && <div className="small muted">EAN fora do catálogo Berytus: cadastro manual.</div>}
            <div className="field"><label htmlFor="np-title">Nome</label><input className="input" id="np-title" required value={f.title} onChange={set('title')} /></div>
            <div className="cols" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
              <div className="field"><label htmlFor="np-brand">Marca</label><input className="input" id="np-brand" value={f.brand} onChange={set('brand')} /></div>
              <div className="field"><label htmlFor="np-cat">Categoria</label><input className="input" id="np-cat" value={f.category} onChange={set('category')} /></div>
            </div>
            {!(lookup?.found && lookup.isMedicine) && (
              <label className="small" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="checkbox" className="check" checked={isMedicine} onChange={(e) => setIsMedicine(e.target.checked)} /> É medicamento
                {isMedicine && <span className="muted"> · fora do catálogo Berytus, fica aguardando a curadoria antes de publicar</span>}
              </label>
            )}
            <div className="cols" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
              <div className="field"><label htmlFor="np-cost">Custo (R$)</label><input className="input" id="np-cost" inputMode="decimal" value={f.cost} onChange={set('cost')} /></div>
              <div className="field"><label htmlFor="np-pmc">PMC (R$)</label><input className="input" id="np-pmc" inputMode="decimal" value={f.pmc} onChange={set('pmc')} /></div>
              <div className="field"><label htmlFor="np-stock">Estoque</label><input className="input" id="np-stock" inputMode="numeric" value={f.stock} onChange={set('stock')} /></div>
            </div>
            {create.error && (limitHit
              ? <div className="error" role="alert">Você chegou ao limite de {t?.limits.products ?? 10} produtos do período de teste. Desative um produto para cadastrar outro, ou contrate o plano.</div>
              : <ErrorBox error={create.error} />)}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn primary" type="submit" disabled={create.isPending}>{create.isPending ? 'Salvando…' : 'Cadastrar'}</button>
              <button className="btn" type="button" onClick={onClose}>Cancelar</button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
